import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { connectSocket } from '../../../sdk/services/socket';
import { useSimulado } from '../../../core/hooks/useSimulado';

export interface ClockProps {
  time?: string;
  /**
   * A superfície em que o relógio está pintado — não o tema.
   *
   * O mesmo componente aparece em dois lugares de natureza diferente, e é a
   * mesma situação do `Wordmark` do rail (ver "App shell" no CLAUDE.md): a
   * regra do produto é **escolher pela superfície em que se está**, não pelo
   * tema.
   *
   * - `surface` (padrão): Topbar e App Bar, que são `bg-surface`/`bg-footer-bg`
   *   e acompanham o tema. Usa tokens, então o relógio acompanha junto.
   * - `dark`: o `SimuladoHeader`, que é o `Header` do antd — escuro nos dois
   *   temas, com todo o conteúdo em `text-white`. Ali o relógio fica claro
   *   sobre escuro sempre.
   *
   * Antes disto os valores eram fixos (`text-white` sobre
   * `rgba(255,255,255,.1)`), o que só funcionava na barra escura: no Topbar em
   * tema claro era branco sobre branco.
   */
  tone?: 'surface' | 'dark';
  onClick?: () => void;
}

export const TestClock = ({ time = 'middle', tone = 'surface', ...props }: ClockProps) => {
  const [remaining, setRemaining] = useState<string>('');
  const {resolucao} = useSimulado();
  const location = useLocation();

  // Lido dentro do `onmessage`, que é criado uma vez por efeito: sem o ref, o
  // handler fecharia sobre a resolução da primeira renderização e mandaria o
  // aluno para o resultado errado (ou para a home, se ainda não houvesse uma).
  const resolucaoRef = useRef(resolucao);
  useEffect(() => { resolucaoRef.current = resolucao }, [resolucao]);

  useEffect(() => {
    const ws = connectSocket(time);

    ws.onmessage = (e) => {
      const msg = JSON.parse(e.data);
      switch (msg.type) {
        case 'waiting':
          if (location.pathname === '/simulado-iniciado') {
            window.location.href = '/home';
          }
          setRemaining(msg.remaining);
          break;

        case 'start':
          setRemaining('Iniciando');
          break;

        case 'tick':
          if (location.pathname !== '/simulado-iniciado') {
            window.location.href = '/simulado-iniciado';
          }
          setRemaining(msg.remaining);
          break;

        case 'stop':
          setRemaining('Parado');
          // Fim do simulado: o servidor já pontuou todos os inscritos
          // (`h.finish`, broadcaster.go) e encerrou a sala.
          //
          // O destino é o RESULTADO do simulado que acabou — é para onde o
          // aluno espera ir ao terminar a prova. A home é só o retorno de
          // emergência para quando não há `registered_id`, caso em que não
          // existe resultado para abrir.
          if (location.pathname === '/simulado-iniciado') {
            const r = resolucaoRef.current;
            window.location.href = r?.registered_id
              ? `/resultado-simulado/${r.registered_id}`
              : '/home';
          }
          break;
      }
    };

    const ping = setInterval(() => ws.send(JSON.stringify({ type: 'ping' })), 30000);

    return () => {
      clearInterval(ping);
      ws.close();
    };
  }, [time, location.pathname]);

  if (!remaining) return null;

  const escuro = tone === 'dark';

  /*
    O servidor manda a frase inteira — "Simulado começa em: 02:14:33" ou
    "Tempo restante: 01:59:07" (broadcaster.go) — e o relógio pintava tudo em
    16px bold com 1,6px de tracking. Resultado medido no Topbar: 340px de 518
    da fileira de controles, 72% do espaço, empurrando o seletor de certame
    para fora.

    Aqui o texto é partido no `:`. O rótulo fica pequeno e secundário, como o
    `caption` dos StatusBadge vizinhos, e só o tempo mantém peso — que é o que
    se lê de relance. Sem `:` (mensagem curta, ou formato futuro diferente), o
    texto inteiro vira o tempo e nada se perde.
  */
  const corte = remaining.indexOf(':');
  const temRotulo = corte > 0 && /\d/.test(remaining.slice(corte));
  const rotulo = temRotulo ? remaining.slice(0, corte).trim() : '';
  const tempo = temRotulo ? remaining.slice(corte + 1).trim() : remaining;

  return (
    <div
      data-testid="test-clock"
      data-tone={tone}
      className={
        'inline-flex items-center gap-2 rounded-chip px-3 py-1 ' +
        (escuro
          ? 'border border-white/20 bg-white/10'
          : // Família neutra de propósito: uma contagem regressiva não é
            // sucesso, aviso nem erro, e o CLAUDE.md reserva verde/laranja/
            // vermelho para os seus próprios significados.
            'border border-edge-strong bg-surface-muted')
      }
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
        className={escuro ? 'text-[#a3e635]' : 'text-ink-secondary'}
      >
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
      <p
        className={'m-0 flex min-w-0 items-baseline gap-1.5 leading-none'}
        {...props}
      >
        {rotulo && (
          <span
            className={
              'truncate text-[11px] font-medium ' +
              (escuro ? 'text-white/70' : 'text-ink-secondary')
            }
          >
            {rotulo}
          </span>
        )}
        <span
          className={
            // `tabular-nums` mantém a largura estável a cada segundo; sem ele
            // o relógio treme e empurra os vizinhos.
            'shrink-0 text-sm font-bold tabular-nums tracking-wide ' +
            (escuro ? 'text-white' : 'text-ink')
          }
        >
          {tempo}
        </span>
      </p>
    </div>
  );
};
